import Http from '@/services/http';
import {
  ProfileToEdit,
  Lookup,
  ProfileAssetRequest,
  ProfileAssetResponse,
  ProfileInstallationRequest,
  ProfileInstallationResponse,
  ProfileCompanyResponse,
  ProfileNameAvailableRequest,
  ProfileNameAvailableResponse,
  ProfileModuleRequest,
  ProfileModuleResponse,
  ProfileSave,
  ProfileLookup,
} from 'sigma-mappings';
import { lowerCaseCompare } from '@/services/stringHelper';

const urls = {
  getAssets: 'perfil/{parentProfileId}/ativo',
  getProfileAssets: '/perfil/{id}/alterar/ativo',
  getInstallations: '/perfil/{parentProfileId}/instalacao',
  getProfileInstallations: '/perfil/{id}/alterar/instalacao',
  getProfileForEdit: id => `/perfil/${id}/alterar`,
  getCompanies: '/perfil/{parentProfileId}/empresa',
  getCompaniesProfile: '/perfil/{id}/alterar/empresa',
  getProfiles: '/perfil/origem',
  getEligibleAdministrators: '/usuario/lookup',
  verifyProfileName: '/perfil/nomedisponivel',
  getModules: '/perfil/{parentProfileId}/modulo',
  getProfileModules: '/perfil/{id}/alterar/modulo',
  createProfile: '/perfil',
  updateProfile: '/perfil',
};

export default {
  /**
   * Recupera o perfil para edição.
   * @param {*} profileId
   * @returns {Promise<*>} Entidade de perfil para edição.
   */
  getProfileForEdit(profileId) {
    const http = new Http();
    return http.get(urls.getProfileForEdit(profileId))
      .then(({ data }) => new ProfileToEdit(data));
  },

  /**
   * Recupera uma lista de ativos, de acordo com o perfil, ou
   * um perfil de origem no caso de inclusão de um novo perfil.
   * @param  {*} profileId
   * @param  {Array} installationsIds
   * @param  {*} parentProfileId
   */
  getProfileAssets({
    profileId,
    installationsIds,
    parentProfileId,
  }) {
    const http = new Http();
    if (profileId) {
      return http.post(
        urls.getProfileAssets.replace(/\{id\}/g, profileId),
        new ProfileAssetRequest({
          installationsIds,
        }),
      ).then(({ data }) => data.map(item => new ProfileAssetResponse(item)));
    }

    return http.post(
      urls.getAssets.replace(/\{parentProfileId\}/g, parentProfileId),
      new ProfileAssetRequest({
        installationsIds,
      }),
    ).then(({ data }) => data.map(item => new ProfileAssetResponse(item)));
  },

  /**
   * Recupera uma lista de instalações, de acordo com um perfil, ou
   * um perfil de origem no caso de uma inclusão de um novo perfil.
   * @param {*} profileId
   * @param {Array} companyIds
   * @param {Array} regionalIds
   * @param {*} parentProfileId
   * @returns {Promise<*>} Lista de instalações.
   */
  getProfileInstallations({
    profileId,
    companyIds,
    regionalIds,
    parentProfileId,
  }) {
    const http = new Http();
    if (profileId) {
      return http.post(
        urls.getProfileInstallations.replace(/\{id\}/g, profileId),
        new ProfileInstallationRequest({
          companyIds,
          regionalIds,
        }),
      )
        .then(({ data }) => data.map(item => new ProfileInstallationResponse(item)));
    }
    return http.post(
      urls.getInstallations.replace(/\{parentProfileId\}/g, parentProfileId),
      new ProfileInstallationRequest({
        companyIds,
        regionalIds,
      }),
    ).then(({ data }) => data.map(item => new ProfileInstallationResponse(item)));
  },

  /**
   * Recupera uma lista de empresas, de acordo com o id do perfil, ou
   * de um perfil de origem no caso de um novo perfil.
   * @param profileId
   * @param parentProfileId
   * @returns {Promise<*>} Lista de empresas.
   */
  getCompaniesProfile({
    profileId,
    parentProfileId,
  }) {
    const http = new Http();
    if (profileId) {
      return http.get(urls.getCompaniesProfile.replace(/\{id\}/g, profileId))
        .then(({ data }) => data.map(item => new ProfileCompanyResponse(item)));
    }
    return http.get(urls.getCompanies.replace(/\{parentProfileId\}/g, parentProfileId))
      .then(({ data }) => data.map(item => new ProfileCompanyResponse(item)));
  },

  /**
   * Recupera uma lista de perfis associados ao usuário.
   * @returns {Promise<*>} Lista de perfi.
   */
  getProfiles() {
    const http = new Http();
    return http.get(urls.getProfiles)
      .then(({ data }) => data.map(item => new ProfileLookup(item)));
  },
  /**
   * Tenta incluir o perfil pai na lista caso ele não esteja.
   * Inclui respeitando a ordenação da lista.
   * @param {*} currentParentProfile
   * @param {*} profiles
   */
  tryIncludeParent(currentParentProfile, profiles) {
    if (!currentParentProfile) {
      return;
    }
    if (!profiles.some(profile => profile.id === currentParentProfile.id)) {
      let previousIndex = profiles.findIndex(profile => profile.name > currentParentProfile.name);
      if (previousIndex < 0) {
        previousIndex = 0;
      }
      profiles.splice(previousIndex, 0, currentParentProfile);
    }
  },

  /**
   * Recupera uma lista de usuários que pertencem aos mesmos perfis do usuário logado
   * @returns {Promise<*>} Lista de usuários
   */
  getEligibleAdministrators() {
    const http = new Http();
    return http.get(urls.getEligibleAdministrators)
      .then(({ data }) => data.map(item => new Lookup(item)));
  },

  /**
   * Verifica se o nome do perfil está disponível para uso
   * @param {*} profileName
   * @param {*} profileId
   * @returns {Promise<*>} retorna um objeto contendo uma propriedade status,
   * verdadeiro quando disponível para uso.
   */
  verifyIfProfileNameIsAvailable(profileName, profileId) {
    let request = {
      profileName,
    };
    if (profileId) {
      request = {
        ...request,
        profileId,
      };
    }

    const http = new Http();
    return http.post(
      urls.verifyProfileName,
      new ProfileNameAvailableRequest(request),
    ).then(({ data }) => new ProfileNameAvailableResponse(data));
  },

  /**
   * Recupera uma lista de modulos de acordo com o do perfil, ou um
   * perfil de origem em caso de um novo perfil.
   * @param {*} profileId
   * @param {Array} assetsIds
   * @param {*} parentProfileId
   * @returns {Promise<*>} Entidade de perfil para edição.
   */
  getProfileModules({
    profileId,
    assetsIds,
    parentProfileId,
  }) {
    const http = new Http();
    if (profileId) {
      return http.post(
        urls.getProfileModules.replace(/\{id\}/g, profileId),
        new ProfileModuleRequest({
          assetsIds,
        }),
      ).then(({ data }) => data.map(item => new ProfileModuleResponse(item)));
    }
    return http.post(
      urls.getModules.replace(/\{parentProfileId\}/g, parentProfileId),
      new ProfileModuleRequest({
        assetsIds,
      }),
    ).then(({ data }) => data.map(item => new ProfileModuleResponse(item)));
  },

  /**
   * Salva os dados do perfil
   * @param {*} profile
   */
  saveProfile(profile) {
    const http = new Http();
    const requestData = this.groupProfileRelationships(profile);
    const payload = new ProfileSave(requestData);
    if (profile.id) {
      return http.put(urls.updateProfile, payload);
    }
    return http.post(urls.createProfile, payload);
  },

  filterCompaniesByInstallations(profile, installations) {
    return profile.companies.map((company) => {
      const groupedCompany = company;
      groupedCompany.installations = [];
      const associatedInstallations = installations.filter(
        installation => lowerCaseCompare(installation.companyId, groupedCompany.id),
      );
      associatedInstallations.forEach(
        (associatedInstallation) => {
          groupedCompany.installations.push(associatedInstallation);
        },
      );
      return groupedCompany;
    }).filter(company => company.installations.length > 0);
  },
  filterInstallationsByAssets(profile, assets) {
    return profile.installations.map((installation) => {
      const groupedInstallation = installation;
      groupedInstallation.assets = [];
      const assetsAssociated = assets.filter(
        asset => lowerCaseCompare(asset.installationId, groupedInstallation.id),
      );
      assetsAssociated.forEach(
        (assetAssociated) => {
          groupedInstallation.assets.push(assetAssociated);
        },
      );
      return groupedInstallation;
    }).filter(installation => installation.assets.length > 0);
  },
  filterAssetsByModules(profile) {
    return profile.assets.map((asset) => {
      const groupedAsset = asset;
      groupedAsset.modules = [];
      const modulesAssociated = profile.modules.filter(
        module => module.associatedAssets.filter(
          associatedAsset => lowerCaseCompare(associatedAsset.id, asset.id),
        ).length > 0,
      );
      modulesAssociated.forEach(
        (moduleAssociated) => {
          groupedAsset.modules.push(moduleAssociated);
        },
      );
      return groupedAsset;
    }).filter(asset => asset.modules.length > 0);
  },
  groupProfileRelationships(profile) {
    const assets = this.filterAssetsByModules(profile);
    const installations = this.filterInstallationsByAssets(profile, assets);
    const companies = this.filterCompaniesByInstallations(profile, installations);
    return {
      id: profile.id,
      name: profile.name,
      description: profile.description,
      administrators: profile.administrators.map(adm => adm.id),
      parentProfileId: profile.parentProfileId,
      companies,
    };
  },
};
